<template>
  <div class="h-full" :id="id">
    <WidgetsContainer :loading="false" @action:expand="goToExpandLink">
      <template #body>
        <template v-if="selectedToken">
          <div>
            <div :class="width > 350 ? 'flex justify-between' : ''" class="mt-2 pb-4">
              <div class="flex items-center space-x-4 bg-gray-900 px-2 pt-1">
                <div
                  v-for="tf in views"
                  :key="tf.id"
                  @click="selectedView = tf.id"
                  class="cursor-pointer rounded text-xs transition-all ease-linear"
                  :class="tf.id === selectedView ? 'font-semibold text-gray-100' : 'text-gray-400 hover:text-gray-100'"
                >
                  {{ tf.title }}
                </div>
              </div>
              <div class="flex items-center space-x-4 bg-gray-900 px-2 pt-1">
                <div
                  v-for="tf in tags"
                  :key="tf.id"
                  @click="selectedTag = tf.id"
                  class="cursor-pointer rounded text-xs transition-all ease-linear"
                  :class="tf.id === selectedTag ? 'font-semibold text-gray-100' : 'text-gray-400 hover:text-gray-100'"
                >
                  {{ tf.title }}
                </div>
              </div>
            </div>
            <!-- disabling this as this component is disabled on production -->
            <!-- <TableBase
              v-if="selectedView == 'table'"
              :config="config"
              :data="filteredData"
              :loading="tableLoader"
              :paginate="getHolderData"
              :key="selectedTag"
              :reset-pagination-page="false"
            /> -->
            <div v-if="selectedView == 'chart'" class="flex overflow-auto pt-4">
              <ChartPie v-if="filteredPieData.length" :series="filteredPieData" :truncate-length="25" />
              <NoDataFound v-else class="flex-1" />
            </div>
          </div>
        </template>
        <template v-else>
          <WidgetsErrorMessage>
            <span v-if="notSupported">Token does not support On-Chain Ownership</span>
            <span v-else>Select a token to load ownership data</span>
          </WidgetsErrorMessage>
        </template>
      </template>
    </WidgetsContainer>
  </div>
</template>
<script setup>
import { ref, computed, watch, onMounted, inject, nextTick } from 'vue';
import ChartPie from '@/components/chart/ChartPie.vue';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import sumBy from 'lodash/sumBy';
import useHttp from '@/composeables/http';

const $http = useHttp();

//INJECTS
const id = inject('id');
const widget = inject('widget');
const connectedToChannel = inject('connected-to-channel');
const widgetChannelOutput = inject('widget-channel-output');
const viewOnlyMode = inject('view-only-mode');
const subscribeOnlyMode = inject('subscribe-only-mode');

//WIDGET
watch(
  () => widget.value.configuration,
  () => {
    loading.value = true;
    setTokenFromConfig();
    setFiltersFromConfig();
    getTokenData();
    getHolderData();
  }
);
watch(connectedToChannel, newVal => {
  if (!newVal) {
    setTokenFromConfig();
  } else {
    setTokenFromChannel();
  }
});
watch(widgetChannelOutput, () => {
  setTokenFromChannel();
});
function setTokenFromChannel() {
  if (widgetChannelOutput.value) {
    const coin = widgetChannelOutput.value[0];
    const onChainToken = onChainTokens.value?.find(x => x.coin_uid == coin.coin_uid);
    if (onChainToken) {
      selectedToken.value = onChainToken;
    } else {
      selectedToken.value = null;
      notSupported.value = true;
    }
  } else {
    if (connectedToChannel) {
      selectedToken.value = null;
    }
  }
}
function goToExpandLink() {
  window.open(`/coin/${widget.value.configuration.coin_uid}`, '_blank');
}
function setTokenFromConfig() {
  if (widget.value.configuration && widget.value.configuration.token) {
    selectedToken.value = widget.value.configuration.token;
  } else {
    notSupported.value = true;
  }
}
function setFiltersFromConfig() {
  if (widget.value.configuration && widget.value.configuration.top_addresses) {
    topAddresses.value = widget.value.configuration.top_addresses;
  }
  if (widget.value.configuration && widget.value.configuration.address_ownership) {
    addressOwnership.value = widget.value.configuration.address_ownership;
  }
}
async function updateWidgetConfig() {
  if (!viewOnlyMode.value) {
    if (subscribeOnlyMode) {
      await await $http.post('/widget_overrides', {
        widget_override: {
          dashboard_widget_id: widget.value.id,
          overrides: {
            token: selectedToken.value
          }
        }
      });
    } else {
      let payload = {
        configuration: {
          ...widget.value.configuration,
          token: selectedToken.value,
          selected_view: selectedView.value,
          selected_tag: selectedTag.value
        }
      };
      if (selectedToken.value) {
        await $http.patch(`/dashboard_widgets/${widget.value.id}`, payload);
      }
    }
  }
}

//TOKEN
const selectedToken = ref(null);
const notSupported = ref(false);
const topAddresses = ref('');
const addressOwnership = ref('');
const onChainTokens = ref([]);
watch(selectedToken, () => {
  loading.value = true;
  getTokenData().then(() => {
    getHolderData();
  });
});

async function getTokens() {
  let response;
  const config = widget.value.configuration;
  if (config && config.token) {
    response = await $http.get('/data/coins_token');
    onChainTokens.value = response.data;
    nextTick(() => {
      setToken();
    });
  }
}
function setToken() {
  notSupported.value = false;
  if (connectedToChannel) {
    setTokenFromChannel();
  } else {
    setTokenFromConfig();
  }
}
onMounted(() => {
  if (widget?.configuration) {
    topAddresses.value = widget.value.configuration?.top_addresses;
    addressOwnership.value = widget.value.configuration?.address_ownership;
  }
});

//TAGS & VIEWS
const selectedView = ref('table');
const views = [
  { id: 'table', title: 'Table' },
  { id: 'chart', title: 'Chart' }
];
const tags = [
  { id: 'top_holders', title: 'Top Holders' },
  { id: 'genesis', title: 'Genesis Wallets' },
  { id: 'earlyHolder', title: 'Early Holders' }
];
const selectedTag = ref('top_holders');
const tagsMap = { genesis: 'Genesis', earlyHolder: 'Early Holder' };
onMounted(() => {
  if (widget?.configuration) {
    selectedView.value = widget.value.configuration?.selected_view
      ? widget.value.configuration.selected_view
      : selectedView.value;
    selectedTag.value = widget.value.configuration?.selected_tag
      ? widget.value.configuration.selected_tag
      : selectedTag.value;
  }
});
watch(selectedTag, () => {
  getHolderData();
  updateWidgetConfig();
});

watch(selectedView, () => {
  updateWidgetConfig();
});

//TABLE CONFIG
const tableLoader = ref(true);
const config = {
  alternateBg: true,
  rowClass: 'py-2 hover:bg-gray-800 duration-100',
  horizontalScroll: false,
  noXOverflow: true,
  useParentContainer: true,
  hideLoadMore: true,
  headerClass: 'py-8 sticky top-0 bg-gray-900 capitalize',
  perPage: 100,
  page: 1,
  cols: [
    {
      type: 'index',
      size: 1,
      name: 'Rank',
      headerClass: 'border-b-0',
      cellClass: 'py-2 w-full'
    },
    {
      type: 'truncate_text',
      size: 5,
      name: 'Address',
      id: 'address',
      headerClass: 'border-b-0',
      truncateLength: 15
    },
    {
      type: 'dollar_format',
      size: 1,
      name: 'Quantity in USD',
      headerClass: 'border-b-0',
      id: 'price',
      cellClass: 'w-10'
    },
    {
      type: 'number',
      size: 4,
      name: 'Quantity',
      id: 'quantity',
      decimals: 2,
      headerClass: 'border-b-0'
    },
    {
      type: 'small_percent',
      size: 3,
      name: 'Ownership %',
      id: 'ownership',
      multiply: true,
      headerClass: 'border-b-0 text-right'
    },
    {
      type: 'text',
      size: 3,
      name: 'Tags',
      id: 'tag',
      headerClass: 'border-b-0 text-right',
      cellClass: 'text-right '
    },
    {
      type: 'link',
      size: 3,
      name: 'Etherscan',
      id: 'link',
      headerClass: 'border-b-0',
      cellClass: 'w-10'
    }
  ]
};

//PIE CHART
const pieLoader = ref(true);
const pieData = computed(() => {
  return holderData.value
    .map(x => {
      return {
        name: x.public_tag || x.address,
        y: (x.amount / tokenData.value.totalSupply) * 100,
        tag: x.tag[0],
        amount: x.amount,
        address: x.address
      };
    })
    .slice(0, 10);
});
const filteredPieData = computed(() => {
  let data = pieData.value;
  data = topAddresses.value ? data.slice(0, topAddresses.value) : data;
  data = uniqBy(data, 'name');
  data = orderBy(
    data.filter(item => item.y >= addressOwnership.value),
    'y',
    'desc'
  );
  if (data.length > 5) {
    let top5 = data.splice(0, 5);
    top5.push({
      name: 'Others',
      y: sumBy(data, 'y')
    });
    return top5;
  } else {
    return data;
  }
});

//TABLE DATA
const loading = ref(false);
const holderData = ref([]);
const tokenData = ref({});
const tableData = computed(() => {
  return uniqBy(
    holderData.value.map(x => {
      return {
        address: x.public_tag || x.address,
        quantity: x.amount,
        tag: tagsMap[x.tag[0]],
        price: tokenData.value.tokenPrice * x.amount,
        ownership: x.amount / tokenData.value.totalSupply,
        link: {
          link: 'https://etherscan.io/address/' + x.address,
          text: 'View Wallet'
        },
        linkAddress: x.address
      };
    }),
    'address'
  );
});
const filteredData = computed(() => {
  if (selectedTag.value == 'top_holders') {
    return orderBy(tableData.value, 'quantity', 'desc');
  } else {
    return orderBy(
      tableData.value.filter(item => item.tag && item.tag.includes(tagsMap[selectedTag.value])),
      'quantity',
      'desc'
    );
  }
});
async function getTokenData() {
  let response;
  const token = selectedToken.value?.token;
  try {
    if (token) {
      tableLoader.value = true;
      response = await $http.get('/data/token_data', {
        params: {
          address: token
        }
      });
      tokenData.value = response.data;
      tableLoader.value = false;
    }
  } catch (error) {
    tableLoader.value = false;
  }
}
async function getHolderData(pageParams = { page: 1, perPage: 100 }) {
  const token = selectedToken.value?.token;
  let response;
  tableLoader.value = true;
  if (pageParams.page == 1) {
    pieLoader.value = true;
  }
  try {
    if (token) {
      response = await $http.get('/data/holder_data', {
        params: {
          page: pageParams.page,
          limit: pageParams.perPage,
          tag: selectedTag.value == 'top_holders' ? '' : selectedTag.value,
          address: token
        }
      });
    }
    if (pageParams.page == 1) {
      holderData.value = response.data.result;
    } else {
      holderData.value.push(...response.data.result);
    }
    if (!response.data.nextPage) {
      pageParams.stopCall = true;
    }
    loading.value = false;
    pieLoader.value = false;
    tableLoader.value = false;
  } catch {
    holderData.value = [];
    tableLoader.value = false;
    loading.value = false;
    pieLoader.value = false;
  }
  return pageParams;
}
onMounted(() => {
  getTokens();
  getTokenData();
  getHolderData();
});
</script>
